import { RefreshCw } from 'lucide-react'
import { StatusBadge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/States'
import { dateTime, duration } from '@/lib/format'
import { cn } from '@/lib/cn'
import type { CrawlRun } from '@/types'

const TILES: Array<{ key: keyof Pick<CrawlRun, 'found' | 'crawled' | 'failed' | 'skipped'>; label: string; tone?: string }> = [
  { key: 'found', label: 'Found' },
  { key: 'crawled', label: 'Crawled' },
  { key: 'failed', label: 'Failed', tone: 'text-brand-red-700' },
  { key: 'skipped', label: 'Skipped' },
]

export function CrawlSummaryBand({
  run,
  loading,
  onRecrawl,
}: {
  run: CrawlRun | null | undefined
  loading: boolean
  onRecrawl: () => void
}) {
  const running = run?.status === 'running'

  if (!loading && !run) {
    return (
      <Card>
        <EmptyState
          title="This client has never been crawled"
          description="Check the crawl configuration on the client profile, then start the first run."
          action={
            <Button size="sm" variant="primary" onClick={onRecrawl}>
              Start first crawl
            </Button>
          }
        />
      </Card>
    )
  }

  const progress = run && run.found > 0 ? Math.round((run.crawled / run.found) * 100) : 0

  return (
    <Card>
      <CardHeader
        title="Latest crawl"
        description={
          run ? (
            <span className="flex flex-wrap items-center gap-2">
              <StatusBadge status={run.status} />
              <span className="font-mono text-[12px]">{run.id}</span>
            </span>
          ) : undefined
        }
        actions={
          <Button variant="primary" disabled={running} onClick={onRecrawl}>
            <RefreshCw size={13} aria-hidden className={cn(running && 'animate-spin')} />
            {running ? 'Crawl running…' : 'Re-crawl site'}
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-px bg-rule sm:grid-cols-4">
        {TILES.map((tile) => (
          <div key={tile.key} className="bg-surface px-4 py-3">
            <p className="field-label">{tile.label}</p>
            <p className={cn('tabular mt-0.5 font-head text-[22px] font-semibold', tile.tone)}>
              {loading || !run ? '—' : run[tile.key]}
            </p>
          </div>
        ))}
      </div>

      {run && (
        <div className="space-y-2 border-t border-rule px-4 py-3">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunk">
            <div
              className="h-full rounded-full bg-affirm transition-[width]"
              style={{ width: `${progress}%` }}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Pages crawled"
            />
          </div>
          <dl className="flex flex-wrap gap-x-6 gap-y-1 text-[12px] text-ink-muted">
            <span className="flex gap-1.5">
              <dt className="text-ink-faint">Started</dt>
              <dd>{dateTime(run.startedAt)}</dd>
            </span>
            <span className="flex gap-1.5">
              <dt className="text-ink-faint">Finished</dt>
              <dd>{run.finishedAt ? dateTime(run.finishedAt) : 'in progress'}</dd>
            </span>
            <span className="flex gap-1.5">
              <dt className="text-ink-faint">Duration</dt>
              <dd className="tabular">{duration(run.startedAt, run.finishedAt)}</dd>
            </span>
          </dl>
        </div>
      )}
    </Card>
  )
}
